// SessionManager.js
// React component for tracking user activity, showing inactivity modal, and handling token refresh.
import React, { useEffect, useRef, useState } from "react";
import { COUNTDOWN_DURATION_MINUTES } from "../config";
import { API_URL } from "../config";
import { useAuth } from "../App";
import InactivityModal from "./InactivityModal";
import SessionToast from "./SessionToast";

const WARNING_BEFORE_EXPIRY_MINUTES = 5;
const COUNTDOWN_MS = COUNTDOWN_DURATION_MINUTES * 60 * 1000;

function getTokenExpiry(accessToken) {
  if (!accessToken) return null;
  try {
    const payload = accessToken.split(".")[1];
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    const { exp } = JSON.parse(jsonPayload);
    return exp ? exp * 1000 : null; // ms
  } catch (e) {
    return null;
  }
}

function SessionManager({ children }) {
  const [toastOpen, setToastOpen] = useState(false);
  const [expiryDisplay, setExpiryDisplay] = useState(null);
  const [tokenVersion, setTokenVersion] = useState(0);
  // Always call all hooks; use a flag to skip modals/toast on /signin route
  const isSigninRoute =
    typeof window !== "undefined" &&
    window.location.pathname.startsWith("/signin");
  const { fetchWithAuth } = useAuth() || {};
  const [inactive, setInactive] = useState(false);
  const [countdown, setCountdown] = useState(COUNTDOWN_MS);
  const countdownRef = useRef();
  const countdownIntervalRef = useRef();
  const inactiveRef = useRef(false);

  // Reset inactivity timer on user activity
  // Reset modal state and countdown; do not use INACTIVITY_MS.
  const resetInactivity = () => {
    setInactive(false);
    setCountdown(COUNTDOWN_MS);
    if (countdownIntervalRef.current)
      clearInterval(countdownIntervalRef.current);
    // No timers needed; modal is triggered by expiry-driven logic.
  };

  // Listen for activity events, but ignore them if modal is open
  // Keep inactiveRef in sync with inactive state
  useEffect(() => {
    inactiveRef.current = inactive;
  }, [inactive]);

  // Dynamically set inactivity warning based on token expiry
  useEffect(() => {
    const events = ["mousemove", "keydown", "mousedown", "touchstart"];
    const activityHandler = () => {
      if (!inactiveRef.current) {
        resetInactivity();
      }
    };
    events.forEach((e) => window.addEventListener(e, activityHandler));

    // Calculate when to show modal based on token expiry
    const auth = JSON.parse(
      window.localStorage.getItem("ftl_auth") || "{}"
    );
    const expiryMs = getTokenExpiry(auth.access_token);
    if (expiryMs) {
      // console.log(
      //   "[SessionManager] Token expiry:",
      //   new Date(expiryMs).toLocaleString(),
      //   `(ms: ${expiryMs})`
      // );
    }
    let warningTimeout;
    if (expiryMs) {
      const warningTimeMs =
        expiryMs - Date.now() - WARNING_BEFORE_EXPIRY_MINUTES * 60 * 1000;
      if (warningTimeMs > 0) {
        warningTimeout = setTimeout(() => {
          setInactive(true);
          setCountdown(expiryMs - Date.now());
        }, warningTimeMs);
      } else {
        // If already within 5 min of expiry, show modal immediately
        setInactive(true);
        setCountdown(expiryMs - Date.now());
      }
    }

    return () => {
      events.forEach((e) => window.removeEventListener(e, activityHandler));
      if (countdownIntervalRef.current)
        clearInterval(countdownIntervalRef.current);
      if (warningTimeout) clearTimeout(warningTimeout);
    };
  }, [tokenVersion]);

  // Countdown logic
  useEffect(() => {
    if (inactive) {
      // When modal opens, set countdown to actual time remaining until expiry
      const auth = JSON.parse(
        window.localStorage.getItem("ftl_auth") || "{}"
      );
      const expiryMs = getTokenExpiry(auth.access_token);
      let timeLeft = expiryMs ? expiryMs - Date.now() : COUNTDOWN_MS;
      countdownRef.current = timeLeft;
      setCountdown(timeLeft);
      countdownIntervalRef.current = setInterval(() => {
        countdownRef.current -= 1000;
        setCountdown(countdownRef.current);
        if (countdownRef.current <= 0) {
          clearInterval(countdownIntervalRef.current);
          handleLogout();
        }
      }, 1000);
    } else {
      if (countdownIntervalRef.current)
        clearInterval(countdownIntervalRef.current);
      setCountdown(COUNTDOWN_MS);
    }
    // eslint-disable-next-line
  }, [inactive]);

  // Refresh token
  // Use plain fetch for refresh endpoint, do NOT send Authorization header
  const refreshSession = async () => {
    try {
      // let headers = { accept: "application/json" };
      // Do NOT set the refresh_token cookie from the frontend.
      // The backend issues a secure HttpOnly refresh_token cookie on login, which the browser will send automatically.
      // If you do not see the cookie after login, the backend is not setting it correctly.

      // headers["Authorization"] = `Bearer ${auth.access_token}`;
      // console.log("[SessionManager] Access token:", auth.access_token);
      const headers = {
        accept: "application/json",
      };

      // console.log("[SessionManager] Refreshing session...");
      // console.log("[SessionManager] Headers:", headers);
      const res = await fetch(`${API_URL}/auth/refresh`, {
        method: "POST",
        credentials: "include",
        headers,
      });
      if (!res.ok) {
        throw new Error(`Refresh failed: ${res.status}`);
      }
      // Expect new access token in response JSON
      const data = await res.json();
      if (data && data.access_token) {
        // Update localStorage and state with new token
        const auth = JSON.parse(
          window.localStorage.getItem("ftl_auth") || "{}"
        );
        const newAuth = { ...auth, access_token: data.access_token };
        window.localStorage.setItem("ftl_auth", JSON.stringify(newAuth));
        setInactive(false);
        setCountdown(getTokenExpiry(data.access_token) - Date.now());
        setToastOpen(true);
        setExpiryDisplay(
          new Date(getTokenExpiry(data.access_token)).toLocaleString()
        );
        setTokenVersion((v) => v + 1); // retrigger expiry logic
        // Modal will close, and useEffect will pick up new expiry
      } else {
        throw new Error("No access_token in refresh response");
      }
    } catch (e) {
      handleLogout();
    }
  };

  // Logout logic
  const handleLogout = () => {
    // Clear cookies/localStorage and redirect to login with redirect param
    const currentPath =
      window.location.pathname + window.location.search + window.location.hash;
    window.localStorage.clear();
    window.location.href = `/signin?redirect=${encodeURIComponent(
      currentPath
    )}`;
  };

  // Detect page reloads/unmounts
  useEffect(() => {
    const beforeUnloadHandler = (e) => {};
    window.addEventListener("beforeunload", beforeUnloadHandler);
    return () => {
      window.removeEventListener("beforeunload", beforeUnloadHandler);
    };
  }, []);

  // Force toast to auto-close after 3 seconds
  useEffect(() => {
    if (toastOpen) {
      const timer = setTimeout(() => setToastOpen(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [toastOpen]);

  return (
    <>
      {children}
      {!isSigninRoute && (
        <>
          <InactivityModal
            open={inactive}
            countdown={countdown}
            onStayLoggedIn={refreshSession}
            onLogout={handleLogout}
          />

          {toastOpen && (
            <div
              style={{
                position: "fixed",
                top: 24,
                left: 0,
                width: "100%",
                display: "flex",
                justifyContent: "center",
                zIndex: 2000,
                pointerEvents: "none",
              }}
            >
              <div
                style={{
                  background: "#d0f5e8",
                  color: "#146356",
                  padding: "14px 32px",
                  borderRadius: 8,
                  fontWeight: 600,
                  fontSize: 18,
                  boxShadow: "0 4px 16px #0002",
                  pointerEvents: "auto",
                }}
              >
                Session extended successfully!
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}

export default SessionManager;
